// The person's trameria cloud (sandboxes) among their agent devices, through
// Me's `deviceDecor`: the sandbox's device is "trameria" (the development one
// "dev") with trameria's mark, and the chat's pill and the line above a chat
// say its state (running, asleep, being woken, waiting for room).
//
// Promise:
// - The browser only shows the state: it offers no sandbox to make, and has
//   no "Wake", "Delete" or "Update CLIs" (the browser has nothing to do with
//   devices; those move to the app).
// - The state is the server's row, read again on `cloud.status` (part.tsx);
//   the screen never guesses ahead of it.

import { useQuery } from "@tanstack/react-query";
import type {
  MeCloudResponse,
  SandboxDTO,
} from "@trameria/cloud-api/sandboxes-shared";
import { SANDBOX_PROFILES } from "@trameria/cloud-api/sandboxes-shared";
import {
  BrandMark,
  type DeviceDecorView,
  type DeviceLook,
  useApi,
} from "@trameria/ui-core";
import { meCloudQuery } from "./queries";
import {
  chatLineWords,
  DEVICE_LABEL,
  hasReported,
  phaseLabel,
  phaseOf,
} from "./words";

/** trameria's mark at the size of the text beside it. Decoration: the name says it. */
export function SandboxMark() {
  return (
    <span
      data-sandbox-mark=""
      className="inline-flex h-[1em] w-[1em] flex-none [&>svg]:h-full [&>svg]:w-full"
    >
      <BrandMark size={16} />
    </span>
  );
}

const MARK = <SandboxMark />;

/** A device as Me's agents part has it. */
type AgentDeviceDTO = Parameters<DeviceDecorView["of"]>[0];

/** The sandbox whose device this is (a deleted one included until its device goes). */
function sandboxOf(
  mine: MeCloudResponse | undefined,
  deviceId: string,
): SandboxDTO | undefined {
  if (!mine) return undefined;
  return SANDBOX_PROFILES.map((p) => mine[p]).find(
    (s) => s.deviceId === deviceId,
  );
}

/** Me's `deviceDecor.useDecor`. */
export function useSandboxDecor(): DeviceDecorView {
  const mine = useQuery(meCloudQuery(useApi())).data;
  return {
    offers: [],
    of(device) {
      const s = sandboxOf(mine, device.id);
      return s ? lookOf(s, device) : null;
    },
  };
}

function lookOf(s: SandboxDTO, device: AgentDeviceDTO): DeviceLook {
  const phase = phaseOf(s, hasReported(device));
  const line = chatLineWords(s, phase);
  return {
    label: DEVICE_LABEL[s.profile],
    mark: MARK,
    dot: phase === "running" ? "ready" : phase === "error" ? "failed" : "off",
    status: phaseLabel(phase, s.queuePosition),
    failed: phase === "error",
    chatLine: line?.text ?? null,
  };
}
